import React, { useMemo } from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import RecordTable, { RecordColumn } from "../common/RecordTable";
import { displayLabPanelName } from "../../utils/labPanelName";

/**
 * The Logic Builder listing, on the platform's record table.
 *
 * Was a hand-rolled header over `LogicCard` rows, with the service filters as
 * toolbar pills above it. It is now `common/RecordTable` like the History and
 * Lab Builder listings: the columns declare how to read and filter themselves,
 * so Service type and Service(s) filter from their own headers and the
 * Clear-filters pill sits on the tab line instead of pushing the rows down.
 *
 * `LogicCard` is gone with it — a row is four cells, and the only one needing
 * markup is the status badge below.
 */
interface SavedLogicsScreenProps {
  filteredLogics: any[];
  onLogicClick: (logic: any) => void;
  /** Where the table portals its "Clear filters" pill — the tab line. */
  clearFiltersSlot?: HTMLElement | null;
}

/** `2 conditions, 5 parameters` — the shape of a logic's first rule. */
const formatRulesSummary = (logic: any): string => {
  const conditions = logic.rules?.list?.[0]?.conditions;
  if (!conditions) return "N/A";
  const parameters = conditions.reduce(
    (total: number, condition: any) => total + (condition.fields?.length || 0),
    0
  );
  return `${conditions.length} condition${
    conditions.length !== 1 ? "s" : ""
  }, ${parameters} parameter${parameters !== 1 ? "s" : ""}`;
};

/**
 * Every service and lab panel a logic prescribes, for the cell and its filter.
 * A logic's lab-panel snapshot carries the internal "<name>.<guid>" in `name`,
 * so the panel name is cleaned for display.
 */
const logicServiceNames = (logic: any): string[] => [
  ...(logic.services || []).map((service: any) => service.name).filter(Boolean),
  ...(logic.labPanels || []).map((panel: any) =>
    displayLabPanelName(panel.name)
  ),
];

const SavedLogicsScreen = ({
  filteredLogics,
  onLogicClick,
  clearFiltersSlot,
}: SavedLogicsScreenProps) => {
  const theme: Theme = useTheme();

  const columns = useMemo<RecordColumn<any>[]>(
    () => [
      {
        key: "name",
        label: "Name",
        flex: 1,
        searchable: true,
        value: (logic) => logic.name,
      },
      {
        key: "status",
        label: "Status",
        flex: 0.75,
        cellStyle: "node",
        optionKey: (logic) => (logic.enabled === 1 ? "enabled" : "disabled"),
        value: (logic) => (logic.enabled === 1 ? "Enabled" : "Disabled"),
        render: (logic) => {
          const isEnabled = logic.enabled === 1;
          return (
            <div style={styles.statusBadge(theme, isEnabled)}>
              <div style={styles.statusDot(theme, isEnabled)} />
              {isEnabled ? "Enabled" : "Disabled"}
            </div>
          );
        },
      },
      {
        key: "rules",
        label: "Rules summary",
        flex: 1,
        value: (logic) => formatRulesSummary(logic),
      },
      {
        key: "services",
        label: "Service(s)",
        flex: 1.5,
        // A logic prescribes several services, so it matches any selected one
        // — the same multi-valued filter the Prescription tab's Services
        // column uses.
        optionsOf: (logic) =>
          logicServiceNames(logic).map((name) => ({ key: name, label: name })),
        value: (logic) => {
          const names = logicServiceNames(logic);
          if (names.length === 0) return "N/A";
          return names.join(", ");
        },
      },
    ],
    [theme]
  );

  return (
    <RecordTable<any>
      columns={columns}
      rows={filteredLogics}
      rowKey={(logic) => logic.id}
      onRowClick={onLogicClick}
      clearFiltersSlot={clearFiltersSlot}
      emptyMessage="No logics found matching your search"
    />
  );
};

export default SavedLogicsScreen;

const styles = {
  statusBadge: (theme: Theme, isEnabled: boolean) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 8px",
    borderRadius: "8px",
    border: `1px solid ${
      isEnabled ? theme.colorTokens.S[80] : theme.colorTokens.D[80]
    }`,
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    whiteSpace: "nowrap" as "nowrap",
  }),
  statusDot: (theme: Theme, isEnabled: boolean) => ({
    width: "6px",
    height: "6px",
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: isEnabled
      ? theme.colorTokens.S[70]
      : theme.colorTokens.D[70],
  }),
};
